<template>
    <global-modal v-model="isOpened" width="480px">
        <div class="project-name">
            <h4 class="project-name-headline">{{ config.texts.headline }}</h4>
            <form @submit.prevent="submit">
                <div class="form-group">
                    <label class="project-name-label" for="project-name-input">{{ config.texts.label }}</label>
                    <input id="project-name-input" ref="input" class="form-control" type="text"
                           maxlength="190" autocomplete="off" v-model="name">
                </div>
                <button class="btn btn-primary small" type="submit" :disabled="name.trim() === ''">
                    {{ config.texts.submit }}
                </button>
            </form>
        </div>
    </global-modal>
</template>

<script setup lang="ts">
import {GlobalModal} from '@webstacz/shop-components';
import {nextTick, reactive, ref, useTemplateRef} from 'vue';

export type ProjectNameConfig = {
    action: string;
    method: 'POST' | 'PUT';
    token: string;
    name?: string;
    texts: {
        headline: string;
        label: string;
        submit: string;
    };
};

const config = reactive<ProjectNameConfig>({
    action: '',
    method: 'POST',
    token: '',
    texts: {headline: '', label: '', submit: ''},
});

const isOpened = ref(false);
const name = ref('');
const inputRef = useTemplateRef<HTMLInputElement>('input');

/**
 * Posts a real form so the existing store and update actions keep handling the
 * write, with their validation, redirect and status message. PUT travels as the
 * usual method override because a browser form cannot send it directly.
 *
 * @returns {void}
 */
const submit = (): void => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.action;
    form.hidden = true;

    const fields: Record<string, string> = {_token: config.token, name: name.value.trim()};
    if (config.method === 'PUT') {
        fields._method = 'PUT';
    }

    for (const [key, value] of Object.entries(fields)) {
        const input = document.createElement('input');
        input.name = key;
        input.value = value;
        form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
};

window.openProjectNameModal = (modalConfig: ProjectNameConfig): void => {
    Object.assign(config, modalConfig);
    name.value = modalConfig.name ?? '';
    isOpened.value = true;

    nextTick(() => inputRef.value?.focus());
};
</script>

<style scoped lang="scss">
// GlobalModal already pads its body, so anything here is added on top of it
.project-name-headline {
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    // keeps a long headline clear of the modal's close button
    margin: 0 24px 16px 0;
}

.project-name-label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
}

.btn {
    border: 0;

    &:disabled {
        opacity: .45;
        cursor: not-allowed;
    }
}
</style>
